// What the client is willing to believe a response contains.
//
// This module is the mirror image of `packages/api/src/views.ts`, and it is written the
// same way and for a stronger reason. That module decides what leaves the server. This one
// decides what EXISTS in the browser at all — and acceptance case 4 is not "we do not
// render context items today", it is "there is no code path that would render one if it
// appeared in a response".
//
// You cannot make that promise by being careful in components. A component renders what it
// is given, and `{...message}` spread into a `<pre>` during some future debugging session
// is one line. The promise is only structural if the extra key never becomes a value in
// this process. So:
//
//   * every decoder below names its fields ONE AT A TIME off a `Record<string, unknown>`;
//   * no decoder spreads, `Object.assign`es, or iterates the keys of what it was handed;
//   * the decoded object is frozen, and its type has no index signature, so a component
//     that wants `decision.revisions` does not compile and a component that reaches for it
//     dynamically finds `undefined`;
//   * `test/absence.test.ts` feeds every response a `contextItems`, a `telemetry`, a
//     `revisions` and a `reasoning` key full of sentinels, renders the whole application,
//     and asserts that no sentinel reaches the document. It also greps this package's
//     source for those names.
//
// The cost is that a field genuinely added to the API is invisible here until someone adds
// a line. That is the failure that is safe, and it is the same trade `views.ts` makes.
//
// ---------------------------------------------------------------------------
// `facilitatorBusy`
// ---------------------------------------------------------------------------
//
// `docs/archived/09-web-client.md` asks the plan read for a coarse "an activation is running for
// this plan", set server-side while a lease is held, because the client cannot infer it.
// It is in `VIEW_KEYS.plan` now, and it means: an activation lease exists and has not
// expired. An EXPIRED lease reads false — a dead worker is not a thinking facilitator, and
// a plan whose worker crashed must not look permanently thoughtful.
//
// It is read DEFENSIVELY all the same — absent, null, or not a boolean all mean false —
// because the failure that direction is a missing indicator and the failure the other
// direction is a family watching a facilitator that never stops considering. The server
// spent a rule on making a stale lease read false; a client that read a missing field as
// true would undo it from the other end.
//
// WHAT IT DOES NOT DO IS ARRIVE ON ITS OWN. The lease columns are excluded from the
// delivery layer's change fingerprint, so taking a lease signals nobody. See
// `plan/usePlan.ts` for what this client does about that and why.
//
// ---------------------------------------------------------------------------
// `self`
// ---------------------------------------------------------------------------
//
// The second field this contract grew, and the only one anywhere in this client that says
// who the reader is. It rides on the PERSON, not on a session, not on a `/me` call and not
// on the message: an account holds a different roster entry in every plan, so the answer is
// only true of the plan it arrived with, and holding it anywhere else would be holding a
// piece of identity that can disagree with the server about whose words these are.
//
// What it deliberately is not is an account id. The API has never named an account in a
// response and does not start now — `accountId` on the roster would tell everyone in the
// plan which entries are backed by which accounts, which is an identifier that spans
// plans, in order to answer a question about the reader. One bit, about yourself, is the
// whole of it, and `test/absence.test.tsx` greps this package to keep the other version out.

/** A JSON object, before anything has been believed about it. */
type Raw = Record<string, unknown>;

function object(value: unknown): Raw {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new MalformedResponse('expected an object');
  }
  return value as Raw;
}

function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    throw new MalformedResponse('expected an array');
  }
  return value;
}

function str(raw: Raw, key: string): string {
  const value = raw[key];
  if (typeof value !== 'string') {
    throw new MalformedResponse(`expected ${key} to be a string`);
  }
  return value;
}

function nullableStr(raw: Raw, key: string): string | null {
  const value = raw[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'string') {
    throw new MalformedResponse(`expected ${key} to be a string or null`);
  }
  return value;
}

function nullableNum(raw: Raw, key: string): number | null {
  const value = raw[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'number') {
    throw new MalformedResponse(`expected ${key} to be a number or null`);
  }
  return value;
}

function bool(raw: Raw, key: string): boolean {
  const value = raw[key];
  if (typeof value !== 'boolean') {
    throw new MalformedResponse(`expected ${key} to be a boolean`);
  }
  return value;
}

/** Absent, null, or not a boolean all mean the same thing: no. */
function optionalBool(raw: Raw, key: string): boolean {
  return raw[key] === true;
}

export class MalformedResponse extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedResponse';
  }
}

// ------------------------------------------------------------------------- the shapes

/**
 * A plan, in five fields.
 *
 * `updatedAt` is deliberately ABSENT, and unlike `role` on a person the server does not send
 * it either — it was removed from `VIEW_KEYS.plan` at the same time as it was removed from
 * here. It was `plan.updated_at`, a column a database trigger moves on every write to the
 * plan row: the activation watermark, the stuck marker, every plan event, and the worker's
 * lease heartbeat, every thirty seconds for as long as the facilitator is working. It never
 * meant "when this plan last changed". Nothing in this client ever rendered it, which is the
 * only reason it was harmless — the day a screen drew "last updated" it would have drawn the
 * heartbeat, to a family, in a browser.
 *
 * Nothing replaces it. This client does not ask when a plan last changed: it refetches when
 * the delivery layer signals, so there is no timestamp to compare and nothing to hold.
 */
export type Plan = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  /**
   * Whether this plan is a scripted demo rather than a family's own plan. Read
   * DEFENSIVELY, same posture as `facilitatorBusy` and `self`: absent, null, or not a
   * boolean all mean false. The failure that direction is a demo shown as ordinary; the
   * other direction is a family's own plan marked "Demo" — the one that must not happen.
   * See `PlanView.isDemo` (`@mor/api`'s `views.ts`) for what the value means server-side.
   */
  readonly isDemo: boolean;
  /** Whether the reader created this plan. Read defensively: anything but true is false. */
  readonly viewerIsCreator: boolean;
  /**
   * What the creator's first conversation has recorded: the existing arrangements and the
   * suggestions for where to begin. Null for everyone but the creator.
   */
  readonly firstConversation: {
    readonly arrangements: string | null;
    readonly suggestions: readonly Suggestion[];
  } | null;
  /**
   * Whether the reader holds `principal` on this plan. Read defensively: absent or not a
   * boolean means false, so a control gated on it fails closed.
   */
  readonly viewerIsPrincipal: boolean;
};

/** One suggestion for where to begin: a specific issue or a whole topic. */
export type Suggestion = {
  readonly id: string;
  readonly kind: 'issue' | 'topic';
  readonly topic: string;
  /** The issue's title for an `issue` suggestion; null for a `topic` suggestion. */
  readonly title: string | null;
  readonly reason: string;
  readonly rank: number;
  /** The explored issue's id, or null while it is unexplored. */
  readonly issue: string | null;
  /** True once the creator has opened it, from the panel or from a reply button. */
  readonly opened: boolean;
};

export type Thread = {
  readonly id: string;
  readonly kind: string;
  readonly acceptingMessages: boolean;
  readonly createdAt: string;
  /**
   * Whether THIS READER has messages in this thread they have not seen.
   *
   * Self-facing only — it never says anything about another participant's read position,
   * which is the line `docs/ARCHITECTURE.md`'s "deliberately not built" section now draws
   * explicitly between this and a read receipt.
   *
   * Read DEFENSIVELY, same posture as `facilitatorBusy` and `self`: absent, null, or not a
   * boolean all mean "not unread". The failure that direction is a missed badge; the other
   * direction is a badge that never clears, which reads as a broken feature rather than a
   * quiet one.
   */
  readonly unread: boolean;
  /** An activation is working on this thread now. Absent or malformed reads false. */
  readonly facilitatorBusy: boolean;
  /** Roster ids of everyone in this thread, the reader included. Empty if not sent. */
  readonly participants: readonly string[];
  /** The key of the topic this conversation belongs to, or null for a plan-level one. */
  readonly topic: string | null;
  /** The issue this conversation is about, or null when it is not an issue conversation. */
  readonly issue: string | null;
};

/** What an issue line says happened. */
export type IssueChange =
  | 'want'
  | 'why'
  | 'next_step'
  | 'arrangements'
  | 'person_added'
  | 'person_updated'
  | 'person_dismissed'
  | 'question_added'
  | 'question_updated'
  | 'question_dismissed'
  | 'check_added'
  | 'check_updated'
  | 'check_dismissed'
  | 'undone';

/** The sections of an issue's workspace, as the server names an entry's kind. */
export type IssueEntryKind =
  | 'intention'
  | 'rationale'
  | 'next_step'
  | 'arrangements'
  | 'involvement'
  | 'question'
  | 'checking';

const ISSUE_ENTRY_KINDS: ReadonlySet<string> = new Set([
  'intention',
  'rationale',
  'next_step',
  'arrangements',
  'involvement',
  'question',
  'checking',
]);

/** One entry of an issue: the issue's id for a single field, the row's id for a list. */
export type IssueEntryRef = { readonly kind: IssueEntryKind; readonly id: string };

/**
 * The roster references a message may carry, all already validated against this plan's
 * roster by the server. Nothing else in `metadata` is read.
 */
export type RecordChangeLine = {
  readonly item: 'person' | 'topic' | 'issue';
  readonly change:
    | 'added'
    | 'renamed'
    | 'address'
    | 'removed'
    | 'settled'
    | 'reopened'
    | 'updated'
    | 'same_say'
    | 'assigned'
    | 'invited'
    | 'takes_part'
    | 'does_not_take_part'
    | 'related'
    | 'unrelated'
    | IssueChange;
  /** A person's name or a topic's label (the new one for a rename). Never an address. */
  readonly name: string;
  /** The text an issue line is about, clipped by the server; present on issue lines. */
  readonly detail?: string;
  /** On an issue line for a later change: true when it replaced a reading the person said was wrong. */
  readonly correction?: boolean;
  /** The entry an issue line changed, so a card can offer Undo; present on issue lines. */
  readonly entry?: IssueEntryRef;
  /** The labels of the topics a person was assigned to; present only on `assigned`. */
  readonly labels?: readonly string[];
  /** The old name or label; present exactly when `change` is `renamed`. */
  readonly from?: string;
  /** On an added-issue line: the key of the topic the issue was put on. */
  readonly topic?: string;
  /** On an added-issue line: the new issue's id. */
  readonly decision?: string;
};

const RECORD_CHANGES: Readonly<Record<RecordChangeLine['item'], readonly string[]>> = {
  person: [
    'added',
    'renamed',
    'address',
    'removed',
    'same_say',
    'assigned',
    'invited',
    'takes_part',
    'does_not_take_part',
    'related',
    'unrelated',
  ],
  topic: ['added', 'renamed', 'removed', 'settled', 'reopened', 'updated'],
  issue: [
    'want',
    'why',
    'next_step',
    'arrangements',
    'person_added',
    'person_updated',
    'person_dismissed',
    'question_added',
    'question_updated',
    'question_dismissed',
    'check_added',
    'check_updated',
    'check_dismissed',
    'undone',
  ],
};

export type MessageMetadata = {
  /** Which person a facilitator message spoke to. The room can see who was asked. */
  readonly addressed: string | null;
  /** Who a system message is about. "Seth joined" has a subject and no author. */
  readonly subject: string | null;
  readonly systemKind:
    | 'joined'
    | 'confirmation_resolved'
    | 'confirmation_noted'
    | 'confirmation_withdrawn'
    | 'speaking_notice'
    | 'invited'
    | 'record_card'
    | 'record_edit'
    | 'file_ready'
    | 'file_added'
    | 'opened_from'
    | 'opened_suggestion'
    | null;
  readonly outcome: 'ratified' | 'declined' | null;
  readonly actionType: string | null;
  readonly target: string | null;
  /**
   * Who a ratified confirmation did not write to (issue #155, D96) — empty for every
   * other message, including a decline. Read defensively like every other field here: a
   * missing or malformed value reads as nobody, not as a reason to refuse the message.
   * `MessageRow.tsx` turns this into the permanent line in the thread; it names nobody
   * from this array directly, only from the People list it already reads.
   */
  readonly notWrittenTo: readonly string[];
  /**
   * The lines of a `record_card` (one per change) or the single line of a `record_edit`.
   * Lines that are not a valid shape are dropped; empty for every other message.
   */
  readonly changes: readonly RecordChangeLine[];
  /** The topic a resolved `add_topic` line names, or what an `opened_suggestion` line opened. */
  readonly title: string | null;
  /** True when an accepted suggestion was already on the list. */
  readonly alreadyListed: boolean;
  /** The file a `file_ready` line names. Null on every other message. */
  readonly fileId: string | null;
  readonly fileName: string | null;
  /** The suggestion an `opened_suggestion` line records. Null on every other message. */
  readonly suggestion: string | null;
  /** Short replies Mor offers under its message; empty on every other message. */
  readonly choices: readonly MessageChoice[];
  /** On an `opened_from`: where the issue was opened from. Null on every other message. */
  readonly from: 'first_conversation' | 'home' | 'conversation' | null;
  /** On an `opened_from` from the home page or a conversation: what the person said there. */
  readonly said: string | null;
  /** On an `opened_from` a conversation: the name of that conversation. */
  readonly fromTitle: string | null;
};

/**
 * One reply offered as a button. `finish` also takes the person to the issue's summary;
 * `suggestion` makes the button open that suggestion instead of posting the text; `upload`
 * makes it open the file picker instead.
 */
export type MessageChoice = {
  readonly text: string;
  readonly finish: boolean;
  readonly upload: boolean;
  readonly suggestion?: string;
};

export type Message = {
  readonly id: string;
  readonly threadId: string;
  /** A decimal string. The client compares it and never computes one. */
  readonly seq: string;
  readonly type: 'human' | 'facilitator' | 'system';
  readonly authorId: string | null;
  readonly body: string;
  readonly metadata: MessageMetadata;
  readonly createdAt: string;
};

/**
 * A person in the plan.
 *
 * `role` is deliberately ABSENT. The server sends it — `VIEW_KEYS.person` has seven
 * members and this type has six — and the client drops it on the floor, because "users
 * do not think in terms of principal and interested party, and the interface does not
 * present them". Dropping it here rather than declining to render it in a component is
 * the difference between a rule and a habit: there is no value in this process to label
 * anybody with. `test/contract.test.ts` asserts `role` is not a key of a decoded person.
 *
 * `email` is the field this type gained for issue #138: the address a correction has to
 * reach, read live off this row rather than recalled from a request payload frozen at
 * staging time. It arrives real only when THIS READER currently holds a principal's
 * grant — `views.ts` returns it for every row when the reader is a principal and null
 * for every row otherwise, because a principal is the only person who ever approves
 * sending to one of these addresses and so the only reader who needs to check it first.
 * Present and null for everybody else, never absent, which is what lets
 * `PendingActionRow.tsx` and `PersonRow.tsx` read the same field without a branch for
 * who is asking.
 *
 * `self` is the field this type gained, and it is the only thing in this client that says
 * who is holding the browser. It arrives ON THE ROSTER, in the same response as the names a
 * message is attributed with, which is why nothing in this package stores it: "which of
 * these people am I" is answered by the snapshot being rendered, so there is no identity
 * variable here to disagree with the server, none to survive a change of plan — an account
 * holds a different roster entry in each one — and none to go stale while a plan is open.
 */
export type Person = {
  readonly id: string;
  readonly name: string;
  readonly joined: boolean;
  /**
   * Whether a principal has approved inviting this person. Read the same way `joined` is —
   * a field the server always sends, decoded with `bool` — because it is the other half of
   * the same fact: `joined` says an account is bound, `invited` says a principal decided to
   * write to them. See `PersonView.invited` (`@mor/api`'s `views.ts`) for where it comes
   * from and why it is returned to every reader.
   */
  readonly invited: boolean;
  /** When the invitation was approved (ISO), or null when none has been. */
  readonly invitedAt: string | null;
  /** When the person's account was linked (ISO), or null when it has not been. */
  readonly joinedAt: string | null;
  /**
   * The address to show for a correction to reach, or null. Read DEFENSIVELY, the same
   * posture as the other nullable strings this client decodes (`Objective.state`,
   * `Decision.summary`): a server that omits the field reads as "no address to show"
   * rather than refusing the whole person.
   */
  readonly email: string | null;
  /**
   * Whether this is the person reading. True for exactly one entry in a response.
   *
   * Read DEFENSIVELY: absent, null, or not a boolean all mean false. The failure that
   * direction is your own messages looking like everybody else's; the failure the other
   * direction is somebody else's words attributed to you, in a conversation about a
   * parent's death. Only one of those is survivable, so the default is "not you".
   */
  readonly self: boolean;
  /**
   * Whether this seat is played by a model rather than a person — a demo's character. What
   * `MessageRow.tsx` marks a line with, so a reader can tell which of the names answering
   * them is fictional without holding the seat list in their head.
   *
   * Read DEFENSIVELY, the same posture as `self`, and the default falls the same way for
   * the same reason: absent, null, or not a boolean all mean false. Wrong in this
   * direction is a character's line missing its mark in a conversation the reader was
   * told at the door was fictional; wrong in the other is a real family member's words
   * labelled as a machine's. Only one of those is survivable.
   */
  readonly character: boolean;
  /**
   * The keys of the topics this person is assigned to. Whole only when the reader is a
   * principal (an assignment shows a family member nothing); empty for everyone else.
   */
  readonly topics: readonly string[];
  /**
   * Whether this person has the same say in the plan as the reader's principals. Carried
   * as a bare fact so the screen can name people without a role to label anyone with.
   * Absent reads false.
   */
  readonly sameSay: boolean;
  /** False for someone Mor knows about who does not take part in the plan. */
  readonly takesPart: boolean;
  /** Roster id of whoever last changed this person on the list, or null. */
  readonly changedBy: string | null;
  /** When that change was made (ISO), or null. */
  readonly changedAt: string | null;
  /** When it was added (ISO); equal to `changedAt` if it has never been changed. */
  readonly createdAt: string | null;
  /**
   * How this person is related, seen from the reader: the word as recorded ("wife") and who
   * it is through. `via` null means the person is the reader's own ("Your wife"); otherwise
   * it is the other person's ("Seth's wife"). Null when nothing is recorded about them.
   */
  readonly relation: Relation | null;
};

export type Relation = {
  readonly word: string;
  readonly via: { readonly id: string; readonly name: string } | null;
};

/**
 * The plan's objective: what this whole exercise is for, in words safe for everyone who
 * ever joins. Maintained by the facilitator; nothing here is editable (same rule as the
 * decisions list — corrections happen in the conversation).
 */
export type Objective = {
  readonly id: string;
  readonly state: string | null;
  readonly text: string | null;
  readonly updatedAt: string;
  /** The topic this objective belongs to; null for the plan's own objective. */
  readonly topic: string | null;
};

export type Decision = {
  readonly id: string;
  readonly label: string;
  readonly state: string | null;
  readonly summary: string | null;
  readonly updatedAt: string;
  /** The key of the topic this decision belongs to. */
  readonly topic: string;
  /** Roster id of whoever last changed this topic, or null. */
  readonly changedBy: string | null;
  /** When that change was made (ISO), or null. */
  readonly changedAt: string | null;
  /** When it was added (ISO); equal to `changedAt` if it has never been changed. */
  readonly createdAt: string | null;
  /** What the viewer's own issue conversation says they want; null when nothing is shown. */
  readonly intention: string | null;
  readonly position: IssuePosition | null;
  /** The first question still open on the issue, as the viewer sees it. */
  readonly openQuestion: string | null;
  /** When the viewer last sent a message or made a hand edit in this issue (ISO), or null. */
  readonly lastActedAt: string | null;
  /** True when anyone has opened the issue's own conversation; a missing value reads as false. */
  readonly opened: boolean;
};

/**
 * An open ask the facilitator has put to the people who can answer it.
 *
 * D70: everybody in the conversation sees that an ask is open. Since every ask the
 * facilitator can raise is refused unless it is raised in a principals-only conversation,
 * and the server serves this list filtered to conversations the reader is in, everybody who
 * sees one can answer it. There is no second kind of row here and no bit distinguishing
 * them.
 *
 * `createdAt` is still not rendered anywhere as an age — see `plan/inline.ts`, which places
 * an ask by the conversation rather than by a clock.
 */
export type PendingAction = {
  /** The identifier `resolve` takes. The client only ever hands it back. */
  readonly id: string;
  readonly threadId: string;
  readonly type: string;
  readonly payload: InvitationPayload | MoveToDecidingPayload | TopicPayload | null;
  /** The key of the topic the action's conversation belongs to, or null if plan-level. */
  readonly topic: string | null;
  readonly createdAt: string | null;
  /**
   * Roster ids of the people who have already confirmed this action. Only a move to
   * deciding with several people holding the same say fills it; empty otherwise and when
   * the server does not send it.
   */
  readonly acceptedBy: readonly string[];
};

/**
 * The person-reference payload used by invitation and principal-grant confirmations.
 *
 * It carries no address. Issue #138: a request used to freeze one at staging time, and
 * a correction made afterward could never reach it. Nothing here names an address any
 * more — `PendingActionRow.tsx` looks the address up on the matching `Person` instead,
 * the same way it already looks up the name, so a correction reaches a standing ask with
 * no further machinery. An action of any other type arrives with an empty payload and
 * is rendered as an ask this build cannot show — never with a control built out of
 * fields nobody pinned.
 */
export type InvitationPayload = {
  readonly rosterEntryId: string | null;
};

/**
 * The other payload shape this client understands: the move to deciding, whose ask names
 * everyone being invited as the plan moves. Each entry names a roster reference exactly
 * as an invitation payload does; the address for each invitee is the same person lookup.
 */
export type MoveToDecidingPayload = {
  readonly invitees: readonly InvitationPayload[];
};

/** A topic Mor suggests; the person adds it to the list or declines. */
export type TopicPayload = {
  readonly title: string;
  readonly why: string;
};

export const TOPIC_ACTION = 'add_topic';
export const INVITATION_ACTION = 'send_invitation';
export const INVITATION_RATIFIED = 'ratified';
export const INVITATION_DECLINED = 'declined';
/** Invite the family into a topic. The identifier predates the meaning. */
export const MOVE_ACTION = 'move_to_deciding';
/** Invite one person into a topic. */
export const INVITE_ACTION = 'invite';
export const GRANT_PRINCIPAL_ACTION = 'grant_principal';

/**
 * One entry in `release-notes.json` — not a `/v1` response but a static file this same
 * package ships, so it gets a decoder for the reason every response does: this client
 * believes only what it names one field at a time. `id` is the release pull request's
 * number, stable across regeneration and never shown; a person reads `version` (or, on an
 * older entry, `date`), `title`, and `notes`. See `WhatsNew.tsx`.
 *
 * `version` and `date` are each nullable, and exactly one is ever present. Every entry the
 * release-notes agent writes from now on carries `version`, the repository's build number
 * at the moment the entry was written. The five entries that shipped before that scheme
 * existed still carry `date`, unchanged, and stay that way rather than being backfilled.
 */
export type ReleaseNote = {
  readonly id: string;
  readonly version: number | null;
  readonly date: string | null;
  readonly title: string;
  readonly notes: readonly string[];
};

// ----------------------------------------------------------------------- the decoders

export function decodePlan(value: unknown): Plan {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    createdAt: str(raw, 'createdAt'),
    // No `updatedAt`. A server that still sent one — an older deployment, or somebody who
    // put it back — has it dropped here, because this decoder names its fields one at a
    // time and never spreads. See the note on `Plan`.
    isDemo: optionalBool(raw, 'isDemo'),
    viewerIsCreator: optionalBool(raw, 'viewerIsCreator'),
    firstConversation: decodeFirstConversation(raw['firstConversation']),
    viewerIsPrincipal: optionalBool(raw, 'viewerIsPrincipal'),
  });
}

function decodeFirstConversation(value: unknown): Plan['firstConversation'] {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  return Object.freeze({
    arrangements: nullableStr(raw, 'arrangements'),
    suggestions: Object.freeze(array(raw['suggestions']).map(decodeSuggestion)),
  });
}

function decodeSuggestion(value: unknown): Suggestion {
  const raw = object(value);
  const kind = raw['kind'];
  if (kind !== 'issue' && kind !== 'topic') {
    throw new MalformedResponse('expected kind to be issue or topic');
  }
  return Object.freeze({
    id: str(raw, 'id'),
    kind,
    topic: str(raw, 'topic'),
    title: nullableStr(raw, 'title'),
    reason: str(raw, 'reason'),
    rank: num(raw, 'rank'),
    issue: nullableStr(raw, 'issue'),
    opened: raw['opened'] === true,
  });
}

export function decodeThread(value: unknown): Thread {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    kind: str(raw, 'kind'),
    acceptingMessages: bool(raw, 'acceptingMessages'),
    createdAt: str(raw, 'createdAt'),
    unread: optionalBool(raw, 'unread'),
    facilitatorBusy: optionalBool(raw, 'facilitatorBusy'),
    participants: Array.isArray(raw['participants'])
      ? Object.freeze(
          raw['participants'].filter((id): id is string => typeof id === 'string'),
        )
      : Object.freeze([]),
    topic: nullableStr(raw, 'topic'),
    issue: nullableStr(raw, 'issue'),
  });
}

const MESSAGE_TYPES: ReadonlySet<string> = new Set(['human', 'facilitator', 'system']);

export function decodeMessage(value: unknown): Message {
  const raw = object(value);
  const type = str(raw, 'type');
  if (!MESSAGE_TYPES.has(type)) {
    // An unknown speaker is not rendered as a participant, and it is not rendered as the
    // facilitator either. Refusing the whole message is the only answer that cannot be
    // wrong about who said something.
    throw new MalformedResponse(`unknown message type ${type}`);
  }
  const metadata = raw['metadata'];
  return Object.freeze({
    id: str(raw, 'id'),
    threadId: str(raw, 'threadId'),
    seq: str(raw, 'seq'),
    type: type as Message['type'],
    authorId: nullableStr(raw, 'authorId'),
    body: str(raw, 'body'),
    metadata: decodeMetadata(metadata),
    createdAt: str(raw, 'createdAt'),
  });
}

function decodeMetadata(value: unknown): MessageMetadata {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return Object.freeze({
      addressed: null,
      subject: null,
      systemKind: null,
      outcome: null,
      actionType: null,
      target: null,
      notWrittenTo: Object.freeze([]),
      changes: Object.freeze([]),
      title: null,
      alreadyListed: false,
      fileId: null,
      fileName: null,
      suggestion: null,
      choices: Object.freeze([]),
      from: null,
      said: null,
      fromTitle: null,
    });
  }
  const raw = value as Raw;
  return Object.freeze({
    addressed: nullableStr(raw, 'addressed'),
    subject: nullableStr(raw, 'subject'),
    systemKind:
      raw['systemKind'] === 'joined' ||
      raw['systemKind'] === 'confirmation_resolved' ||
      raw['systemKind'] === 'confirmation_noted' ||
      raw['systemKind'] === 'confirmation_withdrawn' ||
      raw['systemKind'] === 'speaking_notice' ||
      raw['systemKind'] === 'invited' ||
      raw['systemKind'] === 'record_card' ||
      raw['systemKind'] === 'record_edit' ||
      raw['systemKind'] === 'file_ready' ||
      raw['systemKind'] === 'file_added' ||
      raw['systemKind'] === 'opened_from' ||
      raw['systemKind'] === 'opened_suggestion'
        ? raw['systemKind']
        : null,
    outcome:
      raw['outcome'] === 'ratified' || raw['outcome'] === 'declined'
        ? raw['outcome']
        : null,
    actionType: nullableStr(raw, 'actionType'),
    target: nullableStr(raw, 'target'),
    notWrittenTo: decodeRosterIdList(raw['notWrittenTo']),
    changes: decodeChanges(raw),
    title: nullableStr(raw, 'title'),
    alreadyListed: raw['alreadyListed'] === true,
    fileId: nullableStr(raw, 'fileId'),
    fileName: nullableStr(raw, 'fileName'),
    suggestion: typeof raw['suggestion'] === 'string' ? raw['suggestion'] : null,
    choices: decodeChoices(raw['choices']),
    from:
      raw['systemKind'] === 'opened_from' &&
      (raw['from'] === 'first_conversation' ||
        raw['from'] === 'home' ||
        raw['from'] === 'conversation')
        ? raw['from']
        : null,
    said:
      raw['from'] === 'home' || raw['from'] === 'conversation'
        ? nullableStr(raw, 'said')
        : null,
    fromTitle: raw['from'] === 'conversation' ? nullableStr(raw, 'fromTitle') : null,
  });
}

function decodeChoices(value: unknown): readonly MessageChoice[] {
  if (!Array.isArray(value)) {
    return Object.freeze([]);
  }
  return Object.freeze(
    value.flatMap((entry): MessageChoice[] => {
      if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
        return [];
      }
      const raw = entry as Raw;
      const text = raw['text'];
      if (typeof text !== 'string' || text === '') {
        return [];
      }
      const suggestion = raw['suggestion'];
      return [
        Object.freeze({
          text,
          finish: raw['finish'] === true,
          upload: raw['upload'] === true,
          ...(typeof suggestion === 'string' && suggestion !== '' ? { suggestion } : {}),
        }),
      ];
    }),
  );
}

function decodeChangeLine(value: unknown): RecordChangeLine | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  const item = raw['item'];
  const change = raw['change'];
  const name = raw['name'];
  const from = raw['from'];
  if (
    (item !== 'person' && item !== 'topic' && item !== 'issue') ||
    typeof change !== 'string'
  ) {
    return null;
  }
  if (!RECORD_CHANGES[item].includes(change) || typeof name !== 'string') {
    return null;
  }
  if (change === 'renamed' ? typeof from !== 'string' : from !== undefined) {
    return null;
  }
  const labels = raw['labels'];
  const detail = raw['detail'];
  const entry = decodeEntryRef(raw['entry']);
  return Object.freeze({
    item,
    change: change as RecordChangeLine['change'],
    name,
    ...(typeof from === 'string' ? { from } : {}),
    ...(typeof detail === 'string' ? { detail } : {}),
    ...(typeof raw['topic'] === 'string' ? { topic: raw['topic'] } : {}),
    ...(typeof raw['decision'] === 'string' ? { decision: raw['decision'] } : {}),
    ...(typeof raw['correction'] === 'boolean' ? { correction: raw['correction'] } : {}),
    ...(entry === null ? {} : { entry }),
    ...(Array.isArray(labels)
      ? {
          labels: Object.freeze(
            labels.filter((label): label is string => typeof label === 'string'),
          ),
        }
      : {}),
  });
}

function decodeEntryRef(value: unknown): IssueEntryRef | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  const kind = raw['kind'];
  const id = raw['id'];
  if (
    typeof kind !== 'string' ||
    !ISSUE_ENTRY_KINDS.has(kind) ||
    typeof id !== 'string'
  ) {
    return null;
  }
  return Object.freeze({ kind: kind as IssueEntryKind, id });
}

function decodeChanges(raw: Raw): readonly RecordChangeLine[] {
  const lines: unknown[] =
    raw['systemKind'] === 'record_card' && Array.isArray(raw['changes'])
      ? raw['changes']
      : raw['systemKind'] === 'record_edit'
        ? [raw['change']]
        : [];
  return Object.freeze(
    lines.flatMap((line) => {
      const decoded = decodeChangeLine(line);
      return decoded === null ? [] : [decoded];
    }),
  );
}

export function decodePerson(value: unknown): Person {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    joined: bool(raw, 'joined'),
    invited: bool(raw, 'invited'),
    invitedAt: nullableStr(raw, 'invitedAt'),
    joinedAt: nullableStr(raw, 'joinedAt'),
    email: nullableStr(raw, 'email'),
    self: optionalBool(raw, 'self'),
    character: optionalBool(raw, 'character'),
    topics: Array.isArray(raw['topics'])
      ? Object.freeze(
          raw['topics'].filter((key): key is string => typeof key === 'string'),
        )
      : Object.freeze([]),
    sameSay: optionalBool(raw, 'sameSay'),
    // Absent reads as taking part, as every person did before the switch existed.
    takesPart: raw['takesPart'] !== false,
    changedBy: nullableStr(raw, 'changedBy'),
    changedAt: nullableStr(raw, 'changedAt'),
    createdAt: nullableStr(raw, 'createdAt'),
    relation: decodeRelation(raw['relation']),
  });
}

/** Defensive like the other optional fields: anything malformed reads as no label. */
function decodeRelation(value: unknown): Relation | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  const word = raw['word'];
  if (typeof word !== 'string' || word.trim() === '') {
    return null;
  }
  const via = raw['via'];
  if (via === null || typeof via !== 'object' || Array.isArray(via)) {
    return Object.freeze({ word, via: null });
  }
  const id = (via as Raw)['id'];
  const name = (via as Raw)['name'];
  if (typeof id !== 'string' || typeof name !== 'string') {
    return null;
  }
  return Object.freeze({ word, via: Object.freeze({ id, name }) });
}

export function decodeObjective(value: unknown): Objective {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    state: nullableStr(raw, 'state'),
    text: nullableStr(raw, 'text'),
    updatedAt: str(raw, 'updatedAt'),
    topic: nullableStr(raw, 'topic'),
  });
}

export function decodeObjectiveList(value: unknown): Objective[] {
  return array(object(value)['objectives']).map(decodeObjective);
}

export function decodeDecision(value: unknown): Decision {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    label: str(raw, 'label'),
    state: nullableStr(raw, 'state'),
    summary: nullableStr(raw, 'summary'),
    updatedAt: str(raw, 'updatedAt'),
    topic: str(raw, 'topic'),
    changedBy: nullableStr(raw, 'changedBy'),
    changedAt: nullableStr(raw, 'changedAt'),
    createdAt: nullableStr(raw, 'createdAt'),
    intention: nullableStr(raw, 'intention'),
    position: decodePosition(raw['position']),
    openQuestion: nullableStr(raw, 'openQuestion'),
    lastActedAt: nullableStr(raw, 'lastActedAt'),
    opened: raw['opened'] === true,
  });
}

export function decodePendingAction(value: unknown): PendingAction {
  const raw = object(value);
  const type = str(raw, 'type');
  return Object.freeze({
    id: str(raw, 'id'),
    threadId: str(raw, 'threadId'),
    type,
    payload:
      type === INVITATION_ACTION ||
      type === GRANT_PRINCIPAL_ACTION ||
      type === INVITE_ACTION
        ? decodeInvitationPayload(raw['payload'])
        : type === MOVE_ACTION
          ? decodeMovePayload(raw['payload'])
          : type === TOPIC_ACTION
            ? decodeTopicPayload(raw['payload'])
            : null,
    topic: nullableStr(raw, 'topic'),
    createdAt: nullableStr(raw, 'createdAt'),
    acceptedBy: decodeRosterIdList(raw['acceptedBy']),
  });
}

function decodeInvitationPayload(value: unknown): InvitationPayload | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  return Object.freeze({
    rosterEntryId: nullableStr(raw, 'rosterEntryId'),
  });
}

function decodeTopicPayload(value: unknown): TopicPayload | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  if (typeof raw['title'] !== 'string' || typeof raw['why'] !== 'string') {
    return null;
  }
  return Object.freeze({ title: raw['title'], why: raw['why'] });
}

function decodeMovePayload(value: unknown): MoveToDecidingPayload | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const invitees = (value as Raw)['invitees'];
  if (!Array.isArray(invitees)) {
    return Object.freeze({ invitees: Object.freeze([]) });
  }
  return Object.freeze({
    invitees: Object.freeze(
      invitees
        .map((item) => decodeInvitationPayload(item))
        .filter((item): item is InvitationPayload => item !== null),
    ),
  });
}

// ------------------------------------------------------------------ envelope decoders

export function decodePlanEnvelope(value: unknown): Plan {
  return decodePlan(object(value)['plan']);
}

export function decodePlanList(value: unknown): Plan[] {
  return array(object(value)['plans']).map(decodePlan);
}

export function decodeThreadList(value: unknown): Thread[] {
  return array(object(value)['threads']).map(decodeThread);
}

export function decodePersonList(value: unknown): Person[] {
  return array(object(value)['people']).map(decodePerson);
}

export function decodeDecisionList(value: unknown): Decision[] {
  return array(object(value)['decisions']).map(decodeDecision);
}

export function decodePendingActionList(value: unknown): PendingAction[] {
  return array(object(value)['pendingActions']).map(decodePendingAction);
}

/**
 * What resolving a confirmation returns. Who this ratification did not write to no
 * longer rides on this response alone (issue #155, D96): it is on the confirmation's
 * own system message now, which is what survives once this response is gone —
 * `decodeMetadata`'s `notWrittenTo` is that same field, read off the message instead.
 */
export function decodePendingActionEnvelope(value: unknown): PendingAction {
  return decodePendingAction(object(value)['pendingAction']);
}

/**
 * A list of roster references, read defensively like `facilitatorBusy` and `self`: a
 * missing or malformed value reads as an empty list, not as a reason to refuse whatever
 * it was on. Shared by `decodeMetadata`'s `notWrittenTo`.
 */
function decodeRosterIdList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return Object.freeze([]);
  }
  return Object.freeze(value.filter((item): item is string => typeof item === 'string'));
}

export type MessagePage = {
  readonly messages: readonly Message[];
  /** The server's cursor for the next older page. The client never builds one. */
  readonly nextBefore: string | null;
};

export function decodeMessagePage(value: unknown): MessagePage {
  const raw = object(value);
  return Object.freeze({
    messages: Object.freeze(array(raw['messages']).map(decodeMessage)),
    nextBefore: nullableStr(raw, 'nextBefore'),
  });
}

export function decodeMessageEnvelope(value: unknown): Message {
  return decodeMessage(object(value)['message']);
}

export function decodeRedemption(value: unknown): string {
  return str(object(object(value)['redemption']), 'planId');
}

/**
 * What `POST /v1/invitations/address` returns: the address an invitation was mailed to,
 * for `Invite.tsx` to hand the vendor's sign-up as a starting value before anyone has
 * signed in, and whether the plan it names is a demo, so that same screen can say so
 * before the person has any session to ask a plan through. This caller already holds an
 * invitation token — a live credential to the seat itself — so `isDemo` tells them nothing
 * the mail that carried the same token did not already say. See `InvitationAddressView`
 * (`@mor/api`'s `views.ts`) for the fuller reasoning.
 */
export type InvitationAddress = {
  readonly email: string;
  readonly isDemo: boolean;
};

/**
 * Same envelope shape as everything else in this module: named one field at a time off
 * `{ invitation: { email, isDemo } }`, nothing spread, nothing else read off `invitation`
 * even though the server sends nothing else there today. `isDemo` is read DEFENSIVELY,
 * the same posture as `Plan.isDemo`: absent, null, or not a boolean all mean false.
 */
export function decodeInvitationAddress(value: unknown): InvitationAddress {
  const raw = object(object(value)['invitation']);
  return Object.freeze({
    email: str(raw, 'email'),
    isDemo: optionalBool(raw, 'isDemo'),
  });
}

/**
 * The plan id out of a `plan.changed` frame, and nothing else.
 *
 * `at` is in the frame and is not read: the client has no use for when the server noticed,
 * and a signal handler that reads one field it does not need is a signal handler somebody
 * later reads a second field from. Everything about a plan comes from a fetch.
 */
export function decodeSignalPlanId(value: unknown): string {
  return str(object(value), 'planId');
}

/**
 * `release-notes.json` is a plain array, newest entry first, not an object with a key —
 * unlike every list above, which mirrors a `/v1` envelope. So this reads `value` directly
 * rather than pulling a field off it.
 */
export function decodeReleaseNoteList(value: unknown): ReleaseNote[] {
  return array(value).map(decodeReleaseNote);
}

export function decodeReleaseNote(value: unknown): ReleaseNote {
  const raw = object(value);
  const version = nullableNum(raw, 'version');
  const date = nullableStr(raw, 'date');
  if ((version === null) === (date === null)) {
    throw new MalformedResponse('expected exactly one of version or date');
  }
  return Object.freeze({
    id: str(raw, 'id'),
    version,
    date,
    title: str(raw, 'title'),
    notes: Object.freeze(
      array(raw['notes']).map((item) => {
        if (typeof item !== 'string') {
          throw new MalformedResponse('expected a note to be a string');
        }
        return item;
      }),
    ),
  });
}

// ------------------------------------------------------------------------ demo mode

/**
 * One seat in a demo scenario: a person's name, and whether a visitor may take it. See
 * `@mor/api`'s `demo/scenarios.ts` for the `SavedScenario` field this reduces —
 * `ScenarioPerson.playable`.
 */
export type DemoSeat = {
  readonly label: string;
  readonly name: string;
  readonly playable: boolean;
  /** What the picker shows in place of `name` — authored copy, shown only here, and empty
   *  when the scenario's author has not written one. See `@mor/eval`'s `scenario.ts` for
   *  `ScenarioPerson.introduction`, the field this carries. */
  readonly introduction: string;
  /** This seat, named so it reads with nothing else around it — authored copy, used
   *  wherever `{{character}}` is substituted rather than on the picker, and empty when the
   *  scenario's author has not written one. See `@mor/eval`'s `scenario.ts` for
   *  `ScenarioPerson.standaloneIntroduction`, the field this carries. */
  readonly standaloneIntroduction: string;
};

/**
 * One scenario `GET /v1/demo/scenarios` offers: a sentence about the family, the moment
 * a person would arrive at, and every seat. `sentence` and `moment` are authored copy —
 * see `@mor/api`'s `demo/blurbs.ts` — never derived from anything a person typed inside
 * a plan.
 */
export type DemoScenario = {
  readonly id: string;
  readonly name: string;
  readonly sentence: string;
  readonly moment: string;
  /** The words shown once, the first time a person arrives in a plan started from this
   *  scenario — a list of paragraphs, authored copy, empty when the scenario's author has
   *  not written one. See `@mor/eval`'s `scenario.ts` for `SavedScenario.welcome`. */
  readonly welcome: readonly string[];
  readonly seats: readonly DemoSeat[];
};

function decodeDemoSeat(value: unknown): DemoSeat {
  const raw = object(value);
  return Object.freeze({
    label: str(raw, 'label'),
    name: str(raw, 'name'),
    playable: bool(raw, 'playable'),
    introduction: str(raw, 'introduction'),
    standaloneIntroduction: str(raw, 'standaloneIntroduction'),
  });
}

function decodeDemoScenario(value: unknown): DemoScenario {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    sentence: str(raw, 'sentence'),
    moment: str(raw, 'moment'),
    welcome: Object.freeze(
      array(raw['welcome']).map((item) => {
        if (typeof item !== 'string') {
          throw new MalformedResponse('expected a demo welcome line to be a string');
        }
        return item;
      }),
    ),
    seats: Object.freeze(array(raw['seats']).map(decodeDemoSeat)),
  });
}

export function decodeDemoScenarioList(value: unknown): DemoScenario[] {
  return array(object(value)['scenarios']).map(decodeDemoScenario);
}

/**
 * What `POST /v1/demo/scenarios/start` returns: the new plan's id, and the scenario and
 * seat it was started from.
 */
export type DemoStart = {
  readonly planId: string;
  readonly scenarioId: string;
  readonly seatLabel: string;
};

export function decodeDemoStart(value: unknown): DemoStart {
  const raw = object(object(value)['demo']);
  return Object.freeze({
    planId: str(raw, 'planId'),
    scenarioId: str(raw, 'scenarioId'),
    seatLabel: str(raw, 'seatLabel'),
  });
}

/**
 * What `GET /v1/plans/:planId/demo/source` returns: which scenario and seat a demo plan
 * was loaded from — read fresh from the server everywhere it is needed
 * (`PlanScreen.tsx`, `CharacterSheet.tsx`), which is what replaced the per-tab
 * `sessionStorage` `demo-stash.ts` used to hold this in. See `@mor/api`'s
 * `views.ts`'s `DemoSourceView` for why the hash and the starting account are not here.
 */
export type DemoSource = {
  readonly scenarioId: string;
  readonly seatLabel: string;
};

export function decodeDemoSource(value: unknown): DemoSource {
  const raw = object(object(value)['demo']);
  return Object.freeze({
    scenarioId: str(raw, 'scenarioId'),
    seatLabel: str(raw, 'seatLabel'),
  });
}

/** The markdown text of one character sheet, from
 *  `GET /v1/demo/scenarios/:id/sheets/:seat`. */
export function decodeDemoSheet(value: unknown): string {
  return str(object(object(value)['sheet']), 'text');
}

// ------------------------------------------------------------------------- voice

/**
 * What `POST /v1/plans/:planId/voice/session` returns: short-lived credentials for the two
 * speech vendors the browser talks to directly, plus the server-set turn timing. No audio
 * ever passes through the server; see `voice/`.
 */
export type VoiceTokens = {
  readonly assemblyAi: { readonly token: string; readonly expiresInSeconds: number };
  readonly cartesia: {
    readonly token: string;
    readonly expiresInSeconds: number;
    readonly voiceId: string;
    readonly modelId: string;
    readonly version: string;
    readonly speed: number;
    readonly emotion: string;
  };
  readonly turn: { readonly minTurnSilenceMs: number; readonly maxTurnSilenceMs: number };
};

function finiteNum(raw: Raw, key: string): number {
  const value = raw[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new MalformedResponse(`expected ${key} to be a number`);
  }
  return value;
}

export function decodeVoiceTokens(value: unknown): VoiceTokens {
  const raw = object(object(value)['voice']);
  const assembly = object(raw['assemblyAi']);
  const cartesia = object(raw['cartesia']);
  const turn = object(raw['turn']);
  return Object.freeze({
    assemblyAi: Object.freeze({
      token: str(assembly, 'token'),
      expiresInSeconds: finiteNum(assembly, 'expiresInSeconds'),
    }),
    cartesia: Object.freeze({
      token: str(cartesia, 'token'),
      expiresInSeconds: finiteNum(cartesia, 'expiresInSeconds'),
      voiceId: str(cartesia, 'voiceId'),
      modelId: str(cartesia, 'modelId'),
      version: str(cartesia, 'version'),
      speed: finiteNum(cartesia, 'speed'),
      emotion: str(cartesia, 'emotion'),
    }),
    turn: Object.freeze({
      minTurnSilenceMs: finiteNum(turn, 'minTurnSilenceMs'),
      maxTurnSilenceMs: finiteNum(turn, 'maxTurnSilenceMs'),
    }),
  });
}

// ------------------------------------------------------------------------ the topics

export type TopicStage = 'nobody' | 'creator' | 'principals' | 'family';

const TOPIC_STAGES: ReadonlySet<string> = new Set([
  'nobody',
  'creator',
  'principals',
  'family',
]);

/** Someone invited into a topic, and whether they have finished their part of it. */
export type TopicMember = {
  readonly id: string;
  readonly name: string;
  readonly finished: boolean;
};

/** Where the reader stands in one topic. */
export type TopicViewer = {
  readonly assigned: boolean;
  readonly invited: boolean;
  readonly started: boolean;
  readonly finished: boolean;
  /** When the reader last wrote in their own conversation in the topic; null if never. */
  readonly lastWroteAt: string | null;
  /** When the reader started the topic; null until they do. */
  readonly startedAt: string | null;
};

/**
 * One of the plan's eleven topics, as the reader may see it. `members` is empty for a topic
 * the reader has not been invited into, so nothing about who is in it reaches them.
 */
export type TopicView = {
  readonly key: string;
  readonly label: string;
  /** What the topic goes by on the home page and at the head of its own pages. */
  readonly phrase: string;
  /** What the topic covers, written for a principal. */
  readonly description: string;
  /** The "For example" lines under the topic in the catalog. */
  readonly examples: readonly string[];
  readonly members: readonly TopicMember[];
  readonly viewer: TopicViewer;
  readonly stage: TopicStage;
  /** Only the person who made the plan, on a topic nobody has been invited into. */
  readonly canStart: boolean;
  /** Mor suggests starting here. Only ever true for the person who made the plan. */
  readonly suggested: boolean;
  /** Mor's one-line reason for the suggestion; creator only, like `suggested`. */
  readonly suggestedReason: string | null;
  /** Mor's recommended order among the suggested topics: 0 first, null when not suggested. */
  readonly suggestedRank: number | null;
};

function decodeTopicMember(value: unknown): TopicMember {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    finished: optionalBool(raw, 'finished'),
  });
}

export function decodeTopicView(value: unknown): TopicView {
  const raw = object(value);
  const stage = str(raw, 'stage');
  if (!TOPIC_STAGES.has(stage)) {
    throw new MalformedResponse(`unknown topic stage ${stage}`);
  }
  const viewer = object(raw['viewer']);
  return Object.freeze({
    key: str(raw, 'key'),
    label: str(raw, 'label'),
    phrase: str(raw, 'phrase'),
    description: str(raw, 'description'),
    examples: Object.freeze(array(raw['examples']).map((item) => str({ item }, 'item'))),
    members: Object.freeze(array(raw['members']).map(decodeTopicMember)),
    viewer: Object.freeze({
      assigned: optionalBool(viewer, 'assigned'),
      invited: optionalBool(viewer, 'invited'),
      started: optionalBool(viewer, 'started'),
      finished: optionalBool(viewer, 'finished'),
      lastWroteAt: nullableStr(viewer, 'lastWroteAt'),
      startedAt: nullableStr(viewer, 'startedAt'),
    }),
    stage: stage as TopicStage,
    canStart: optionalBool(raw, 'canStart'),
    suggested: optionalBool(raw, 'suggested'),
    suggestedReason: nullableStr(raw, 'suggestedReason'),
    suggestedRank:
      typeof raw['suggestedRank'] === 'number' ? (raw['suggestedRank'] as number) : null,
  });
}

/** `GET /v1/plans/:id/topics`: the list under `topics`, or the bare list. */
export function decodeTopicList(value: unknown): TopicView[] {
  const list = Array.isArray(value) ? value : array(object(value)['topics']);
  return list.map(decodeTopicView);
}

/** What opening, finishing and inviting return: `{ topic }`. */
export function decodeTopicEnvelope(value: unknown): TopicView {
  return decodeTopicView(object(value)['topic']);
}

/** What `POST .../topics/:key/invite` takes: named principals, or the family. */
export type Invite = { readonly people: readonly string[] } | { readonly who: 'family' };

/** One direct change to the record, in the shape `POST /v1/plans/:id/record` takes. */
export type RecordChange =
  | { readonly kind: 'add_person'; readonly name: string; readonly email?: string }
  | { readonly kind: 'rename_person'; readonly person: string; readonly name: string }
  | { readonly kind: 'set_person_email'; readonly person: string; readonly email: string }
  | { readonly kind: 'remove_person'; readonly person: string }
  | {
      readonly kind: 'set_person_takes_part';
      readonly person: string;
      readonly takesPart: boolean;
    }
  | { readonly kind: 'add_decision'; readonly label: string; readonly topic: string }
  | {
      readonly kind: 'set_person_topics';
      readonly person: string;
      readonly topics: readonly string[];
    }
  | {
      readonly kind: 'rename_decision';
      readonly decision: string;
      readonly label: string;
    }
  | { readonly kind: 'remove_decision'; readonly decision: string }
  | {
      readonly kind: 'set_issue_intention';
      readonly issue: string;
      readonly text: string;
      readonly position: IssuePosition | null;
    }
  | {
      readonly kind: 'set_issue_rationale';
      readonly issue: string;
      readonly text: string;
    }
  | {
      readonly kind: 'set_issue_next_step';
      readonly issue: string;
      readonly text: string;
    }
  | {
      readonly kind: 'set_issue_arrangements';
      readonly issue: string;
      readonly text: string;
    }
  | {
      readonly kind: 'set_issue_involvement';
      readonly issue: string;
      readonly person: string;
      readonly text: string;
    }
  | {
      readonly kind: 'dismiss_issue_involvement';
      readonly issue: string;
      readonly involvement: string;
    }
  | {
      readonly kind: 'set_issue_question';
      readonly issue: string;
      readonly question?: string;
      readonly text: string;
      readonly viewSoFar: string;
    }
  | {
      readonly kind: 'dismiss_issue_question';
      readonly issue: string;
      readonly question: string;
    }
  | {
      readonly kind: 'set_issue_checking';
      readonly issue: string;
      readonly item?: string;
      readonly text: string;
      readonly knownSoFar: string;
      readonly answerer: string | null;
      readonly professionalReview: boolean;
      readonly basis?: IssueBasis;
    }
  | {
      readonly kind: 'dismiss_issue_checking';
      readonly issue: string;
      readonly item: string;
    }
  | {
      readonly kind: 'undo_issue_entry';
      readonly issue: string;
      readonly section: IssueEntryKind;
      readonly entry: string;
    };

/**
 * The route answers `{ person }` or `{ decision }`; both pages refetch, so nothing of the
 * body is used. It is only required to be an object.
 */
export function decodeRecordResult(value: unknown): void {
  object(value);
}

/** One file as the server shows it to a reader who can already see it. */
export type FileView = {
  readonly id: string;
  readonly name: string;
  readonly format: string;
  readonly uploadedBy: string | null;
  readonly visibility:
    | { readonly kind: 'person'; readonly personId: string }
    | { readonly kind: 'principals' }
    | { readonly kind: 'everyone' };
  readonly state: 'reading' | 'ready' | 'failed';
  readonly description: string | null;
  readonly createdAt: string;
  readonly canChange: boolean;
};

export function decodeFileView(value: unknown): FileView {
  const raw = object(value);
  const visibility = object(raw['visibility']);
  const kind = str(visibility, 'kind');
  const state = str(raw, 'state');
  if (state !== 'reading' && state !== 'ready' && state !== 'failed') {
    throw new MalformedResponse(`unknown file state ${state}`);
  }
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    format: str(raw, 'format'),
    uploadedBy: nullableStr(raw, 'uploadedBy'),
    visibility: Object.freeze(
      kind === 'person'
        ? { kind: 'person' as const, personId: str(visibility, 'personId') }
        : kind === 'principals' || kind === 'everyone'
          ? { kind }
          : fail(`unknown file visibility ${kind}`),
    ),
    state,
    description: nullableStr(raw, 'description'),
    createdAt: str(raw, 'createdAt'),
    canChange: bool(raw, 'canChange'),
  });
}

function fail(message: string): never {
  throw new MalformedResponse(message);
}

export function decodeFileEnvelope(value: unknown): FileView {
  return decodeFileView(object(value)['file']);
}

export function decodeFileList(value: unknown): FileView[] {
  return array(object(value)['files']).map(decodeFileView);
}

/** Where one note came from, as the decision page names it. */
export type NoteSource =
  | {
      readonly kind: 'conversation';
      readonly thread: 'shared' | 'principals' | 'own';
      /** The label of the topic the conversation belongs to; null for a plan-level one. */
      readonly topic: string | null;
    }
  | { readonly kind: 'file'; readonly fileId: string; readonly name: string };

export type NoteView = {
  readonly text: string;
  /** When the note was written (ISO). */
  readonly at: string;
  readonly source: NoteSource;
  /** Who said a stated conversation note; null for file notes and notes without one speaker. */
  readonly speaker: { readonly name: string; readonly you: boolean } | null;
};

export type DecisionNotes = {
  readonly decision: {
    readonly id: string;
    readonly topic: string;
    readonly label: string;
    readonly state: string | null;
    readonly summary: string | null;
  };
  /** From conversations the reader is in and files they can see. */
  readonly shared: readonly NoteView[];
  /** From the reader's own one-to-one conversations with Mor. */
  readonly own: readonly NoteView[];
};

const CONVERSATION_THREADS: ReadonlySet<string> = new Set([
  'shared',
  'principals',
  'own',
]);

function decodeSpeaker(value: unknown): NoteView['speaker'] {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const name = raw['name'];
  const you = raw['you'];
  if (typeof name !== 'string' || name === '' || typeof you !== 'boolean') return null;
  return Object.freeze({ name, you });
}

function decodeNote(value: unknown): NoteView {
  const raw = object(value);
  const speaker = decodeSpeaker(raw['speaker']);
  const source = object(raw['source']);
  const kind = str(source, 'kind');
  if (kind === 'conversation') {
    const thread = str(source, 'thread');
    if (!CONVERSATION_THREADS.has(thread)) {
      throw new MalformedResponse('expected a known conversation for a note');
    }
    return Object.freeze({
      text: str(raw, 'text'),
      at: str(raw, 'at'),
      source: Object.freeze({
        kind: 'conversation',
        thread: thread as 'shared' | 'principals' | 'own',
        topic: nullableStr(source, 'topic'),
      }),
      speaker,
    });
  }
  if (kind === 'file') {
    return Object.freeze({
      text: str(raw, 'text'),
      at: str(raw, 'at'),
      source: Object.freeze({
        kind: 'file',
        fileId: str(source, 'fileId'),
        name: str(source, 'name'),
      }),
      speaker: null,
    });
  }
  throw new MalformedResponse('expected a known source kind for a note');
}

export function decodeDecisionNotes(value: unknown): DecisionNotes {
  const raw = object(value);
  const decision = object(raw['decision']);
  return Object.freeze({
    decision: Object.freeze({
      id: str(decision, 'id'),
      topic: str(decision, 'topic'),
      label: str(decision, 'label'),
      state: nullableStr(decision, 'state'),
      summary: nullableStr(decision, 'summary'),
    }),
    shared: array(raw['shared']).map(decodeNote),
    own: array(raw['own']).map(decodeNote),
  });
}

export type PersonTopicState =
  'not_part' | 'not_started' | 'not_yet_invited' | 'invited' | 'joined';

const PERSON_TOPIC_STATES: ReadonlySet<string> = new Set([
  'not_part',
  'not_started',
  'not_yet_invited',
  'invited',
  'joined',
]);

/** One topic as the person's page shows it: where this person stands in it. */
export type PersonTopic = {
  readonly key: string;
  readonly label: string;
  readonly started: boolean;
  readonly state: PersonTopicState;
};

export type PersonNotes = {
  readonly person: Person;
  /** Every topic in the plan's order for a principal reader; empty for anyone else. */
  readonly topics: readonly PersonTopic[];
  /** From conversations the reader is in and files they can see. */
  readonly shared: readonly NoteView[];
  /** From the reader's own one-to-one conversations with Mor. */
  readonly own: readonly NoteView[];
};

function decodePersonTopic(value: unknown): PersonTopic {
  const raw = object(value);
  const state = str(raw, 'state');
  if (!PERSON_TOPIC_STATES.has(state)) {
    throw new MalformedResponse('expected a known state for a person’s topic');
  }
  return Object.freeze({
    key: str(raw, 'key'),
    label: str(raw, 'label'),
    started: bool(raw, 'started'),
    state: state as PersonTopicState,
  });
}

export function decodePersonNotes(value: unknown): PersonNotes {
  const raw = object(value);
  return Object.freeze({
    person: decodePerson(raw['person']),
    topics: array(raw['topics']).map(decodePersonTopic),
    shared: array(raw['shared']).map(decodeNote),
    own: array(raw['own']).map(decodeNote),
  });
}

// ------------------------------------------------------------------------ issues

export type IssuePosition = 'decided' | 'tentative' | 'undecided';

function decodePosition(value: unknown): IssuePosition | null {
  return value === 'decided' || value === 'tentative' || value === 'undecided'
    ? value
    : null;
}

/** Where a version came from: its own id, its place in the entry's history, and its turn. */
export type IssueVersionMark = {
  readonly versionId: string;
  /** 1 for the first version of an entry; later ones are changes. */
  readonly seq: number;
  /** The card or divider message this version was written under. */
  readonly turnMessageId: string;
  /** True when this version replaced a reading the person said was wrong. */
  readonly correction: boolean;
};

/** What an entry rests on: said by the person, assumed by them, or known by nobody here. */
export type IssueBasis = 'statement' | 'expectation' | 'unknown';

export type IssueText = IssueVersionMark & { readonly text: string };

export type IssueIntention = IssueText & { readonly position: IssuePosition | null };

export type IssueInvolvement = IssueVersionMark & {
  readonly id: string;
  readonly person: {
    readonly id: string;
    readonly name: string;
    readonly relationship: string | null;
  };
  readonly text: string;
};

export type IssueQuestion = IssueVersionMark & {
  readonly id: string;
  readonly raisedBy: 'person' | 'mor';
  readonly question: string;
  readonly viewSoFar: string;
};

export type IssueChecking = IssueVersionMark & {
  readonly id: string;
  readonly text: string;
  readonly knownSoFar: string;
  readonly answerer: { readonly id: string; readonly name: string } | null;
  readonly professionalReview: boolean;
  readonly settled: boolean;
  readonly basis: IssueBasis;
};

/** One issue's workspace, as the viewer may read it. */
export type IssueView = {
  readonly id: string;
  readonly topic: string;
  readonly title: string;
  /** True for an issue taken off the list: only the id, topic and title are real. */
  readonly removed: boolean;
  readonly intention: IssueIntention | null;
  readonly rationale: IssueText | null;
  readonly nextStep: IssueText | null;
  readonly arrangements: IssueText | null;
  readonly involvement: readonly IssueInvolvement[];
  readonly questions: readonly IssueQuestion[];
  readonly checking: readonly IssueChecking[];
};

function num(raw: Raw, key: string): number {
  const value = raw[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new MalformedResponse(`expected ${key} to be a number`);
  }
  return value;
}

function decodeIssueText(value: unknown): IssueText | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = object(value);
  return Object.freeze({
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    text: str(raw, 'text'),
  });
}

function decodeIssueIntention(value: unknown): IssueIntention | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = object(value);
  return Object.freeze({
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    text: str(raw, 'text'),
    position: decodePosition(raw['position']),
  });
}

function decodeBasis(raw: Raw): IssueBasis {
  const basis = raw['basis'];
  if (basis === undefined) {
    return 'statement';
  }
  if (basis !== 'statement' && basis !== 'expectation' && basis !== 'unknown') {
    throw new MalformedResponse('expected basis to be statement, expectation or unknown');
  }
  return basis;
}

function decodeIssueInvolvement(value: unknown): IssueInvolvement {
  const raw = object(value);
  const person = object(raw['person']);
  return Object.freeze({
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    id: str(raw, 'id'),
    person: Object.freeze({
      id: str(person, 'id'),
      name: str(person, 'name'),
      relationship: nullableStr(person, 'relationship'),
    }),
    text: str(raw, 'text'),
  });
}

function decodeIssueQuestion(value: unknown): IssueQuestion {
  const raw = object(value);
  const raisedBy = raw['raisedBy'];
  if (raisedBy !== 'person' && raisedBy !== 'mor') {
    throw new MalformedResponse('expected raisedBy to be person or mor');
  }
  return Object.freeze({
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    id: str(raw, 'id'),
    raisedBy,
    question: str(raw, 'question'),
    viewSoFar: nullableStr(raw, 'viewSoFar') ?? '',
  });
}

function decodeIssueChecking(value: unknown): IssueChecking {
  const raw = object(value);
  const answerer = raw['answerer'];
  const named = answerer === null || answerer === undefined ? null : object(answerer);
  return Object.freeze({
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    id: str(raw, 'id'),
    text: str(raw, 'text'),
    knownSoFar: nullableStr(raw, 'knownSoFar') ?? '',
    answerer:
      named === null
        ? null
        : Object.freeze({ id: str(named, 'id'), name: str(named, 'name') }),
    professionalReview: optionalBool(raw, 'professionalReview'),
    settled: optionalBool(raw, 'settled'),
    basis: decodeBasis(raw),
  });
}

/** The read answers the view itself, or the view under `issue`; either is accepted. */
export function decodeIssueView(value: unknown): IssueView {
  const outer = object(value);
  const raw = outer['issue'] !== undefined ? object(outer['issue']) : outer;
  return Object.freeze({
    id: str(raw, 'id'),
    topic: str(raw, 'topic'),
    title: str(raw, 'title'),
    removed: raw['removed'] === true,
    intention: decodeIssueIntention(raw['intention']),
    rationale: decodeIssueText(raw['rationale']),
    nextStep: decodeIssueText(raw['nextStep']),
    arrangements: decodeIssueText(raw['arrangements']),
    involvement: Object.freeze(
      array(raw['involvement'] ?? []).map(decodeIssueInvolvement),
    ),
    questions: Object.freeze(array(raw['questions'] ?? []).map(decodeIssueQuestion)),
    checking: Object.freeze(array(raw['checking'] ?? []).map(decodeIssueChecking)),
  });
}

/** One version of one entry, for the history page. Fields that do not apply are null. */
export type IssueHistoryEntry = {
  readonly kind: IssueEntryKind;
  readonly parentId: string;
  readonly versionId: string;
  readonly seq: number;
  readonly createdAt: string;
  readonly turnMessageId: string;
  readonly correction: boolean;
  readonly text: string | null;
  readonly position: IssuePosition | null;
  readonly viewSoFar: string | null;
  readonly knownSoFar: string | null;
  /** The roster id of the person a row is about or who could answer it. */
  readonly personId: string | null;
  readonly professionalReview: boolean;
  readonly settled: boolean;
  readonly dismissed: boolean;
  /** What a checking item rested on; null for every other kind. */
  readonly basis: IssueBasis | null;
};

function decodeHistoryEntry(value: unknown): IssueHistoryEntry {
  const raw = object(value);
  const kind = str(raw, 'kind');
  if (!ISSUE_ENTRY_KINDS.has(kind)) {
    throw new MalformedResponse(`unknown issue entry kind ${kind}`);
  }
  const fields = object(raw['fields']);
  // The question's own words are `question`; every other kind's are `text`.
  const text = nullableStr(fields, 'text') ?? nullableStr(fields, 'question');
  return Object.freeze({
    kind: kind as IssueEntryKind,
    parentId: str(raw, 'parentId'),
    versionId: str(raw, 'versionId'),
    seq: num(raw, 'seq'),
    createdAt: str(raw, 'createdAt'),
    turnMessageId: str(raw, 'turnMessageId'),
    correction: optionalBool(raw, 'correction'),
    text,
    position: decodePosition(fields['position']),
    viewSoFar: nullableStr(fields, 'viewSoFar'),
    knownSoFar: nullableStr(fields, 'knownSoFar'),
    personId: nullableStr(fields, 'personId') ?? nullableStr(fields, 'answererId'),
    professionalReview: optionalBool(fields, 'professionalReview'),
    settled: optionalBool(fields, 'settled'),
    dismissed: optionalBool(fields, 'dismissed'),
    basis:
      fields['basis'] === undefined || fields['basis'] === null
        ? null
        : decodeBasis(fields),
  });
}

/** Every version, newest first; the list may arrive bare or under `history`. */
export function decodeIssueHistory(value: unknown): IssueHistoryEntry[] {
  const list = Array.isArray(value) ? value : array(object(value)['history']);
  return list.map(decodeHistoryEntry);
}
